import OpenAI from 'openai';

export const openAIRepository = () => {
  // ================================
  // Composables
  // ================================
  const { $config } = useNuxtApp();
  const { areas } = storeToRefs(areaStore());

  // ================================
  // Config
  // ================================
  const assistantId = 'asst_edXdTwxWsVZCZjcfqLcSLV7S';
  const openai = new OpenAI({
    apiKey: $config.public.openai.apiKey,
    dangerouslyAllowBrowser: true,
  });

  // ================================
  // Data
  // ================================
  const loadingInitThread = ref(false);

  const threadId = ref<string | null>(null);
  const thread = ref<OpenAI.Beta.Threads.Thread | null>(null);

  // ================================
  // Methods
  // ================================
  const sendMessageToAssistant = async (message: string): Promise<any> => {
    if (!threadId.value) {
      throw new Error('Thread not initialized');
    }

    try {
      // Vérifier s'il y a déjà des messages dans le thread
      const existingMessages = await openai.beta.threads.messages.list(threadId.value);
      const isFirstMessage = existingMessages.data.length === 0;

      // Préparer le contenu du message
      let messageContent = message;
      if (isFirstMessage && areas.value && areas.value.length > 0) {
        const areasContext = `Contexte des zones disponibles: ${JSON.stringify(areas.value)}\n\n`;
        messageContent = areasContext + message;
      }

      // Ajouter le message de l'utilisateur au thread
      await openai.beta.threads.messages.create(threadId.value, {
        role: 'user',
        content: messageContent,
      });

      // Créer et exécuter un run avec l'assistant
      const run = await openai.beta.threads.runs.create(threadId.value, {
        assistant_id: assistantId,
        response_format: {
          type: 'json_object',
        },
      });

      // Attendre que le run soit complété
      let runStatus = await openai.beta.threads.runs.retrieve(threadId.value, run.id);

      while (runStatus.status === 'queued' || runStatus.status === 'in_progress') {
        await new Promise((resolve) => setTimeout(resolve, 1000)); // Attendre 1 seconde
        runStatus = await openai.beta.threads.runs.retrieve(threadId.value, run.id);
      }

      if (runStatus.status === 'completed') {
        // Récupérer les messages du thread
        const messages = await openai.beta.threads.messages.list(threadId.value);

        // Trouver le dernier message de l'assistant
        const assistantMessage = messages.data.find((msg) => msg.role === 'assistant' && msg.run_id === run.id);

        if (assistantMessage && assistantMessage.content[0]?.type === 'text') {
          const textResponse = assistantMessage.content[0].text.value;

          try {
            const jsonResponse = JSON.parse(textResponse);
            return jsonResponse;
          } catch {
            return textResponse;
          }
        }
      }

      throw new Error(`Run failed with status: ${runStatus.status}`);
    } catch (error) {
      throw new Error(`Error sending message to assistant: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  };

  // ================================
  // Lifecycle
  // ================================
  onMounted(async () => {
    thread.value = await openai.beta.threads.create();
    threadId.value = thread.value.id;
  });

  return {
    thread,
    loadingInitThread,
    sendMessageToAssistant,
  };
};
